import { createTheme } from '@mui/material/styles'

// Canva-inspired color tokens
const canvaColors = {
  // Primary colors
  primary: {
    main: '#6C5CE7',
    light: '#A29BFE',
    dark: '#5B4BD6',
    50: '#F4F3FF',
    100: '#EBE9FF',
    200: '#D6D1FF',
  },
  secondary: {
    main: '#00B0FF',
    light: '#40C4FF',
    dark: '#0091EA',
  },

  // Background colors
  background: {
    default: '#F7F8FC',
    paper: '#FFFFFF',
  },

  // Surface scale
  surface: {
    1: '#FFFFFF',
    2: '#F2F4FA',
    3: '#E9EDF7',
    border: '#E1E7F0',
    hover: '#F8F9FB',
  },

  // Interactive states
  interactive: {
    hover: '#F5F6FA',
    pressed: '#EBEDF2',
    focus: '#7BA4FF',
  },

  // Text colors
  text: {
    primary: '#1F2937',
    secondary: '#6B7280',
    disabled: '#9CA3AF',
  },

  // Status colors
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
  },
}

// Extend MUI theme types
declare module '@mui/material/styles' {
  interface Palette {
    surface: {
      1: string
      2: string
      3: string
      border: string
      hover: string
      background: string
      paper: string
    }
    interactive: {
      hover: string
      pressed: string
      focus: string
    }
    brand: Palette['primary']
    customText: Palette['primary']
  }

  interface PaletteOptions {
    surface?: {
      1?: string
      2?: string
      3?: string
      border?: string
      hover?: string
      background?: string
      paper?: string
    }
    interactive?: {
      hover?: string
      pressed?: string
      focus?: string
    }
    brand?: PaletteOptions['primary']
    customText?: PaletteOptions['primary']
  }

  interface TypographyVariants {
    headingLarge: React.CSSProperties
    headingMedium: React.CSSProperties
    headingSmall: React.CSSProperties
    description: React.CSSProperties
    large: React.CSSProperties
    p: React.CSSProperties
    ps: React.CSSProperties
    pt: React.CSSProperties
    label: React.CSSProperties
    labelLarge: React.CSSProperties
    labelSmall: React.CSSProperties
    menuItem: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    headingLarge?: React.CSSProperties
    headingMedium?: React.CSSProperties
    headingSmall?: React.CSSProperties
    description?: React.CSSProperties
    large?: React.CSSProperties
    p: React.CSSProperties
    ps: React.CSSProperties
    pt: React.CSSProperties
    label: React.CSSProperties
    labelLarge: React.CSSProperties
    labelSmall: React.CSSProperties
    menuItem: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    headingLarge: true
    headingMedium: true
    headingSmall: true
    description: true
    large: true
    p: true
    ps: true
    pt: true
    label: true
    labelLarge: true
    labelSmall: true
    menuItem: true
  }
}

// Create the Canva-inspired theme
export const editorTheme = createTheme({
  palette: {
    mode: 'light',
    primary: canvaColors.primary,
    secondary: canvaColors.secondary,
    background: canvaColors.background,
    text: canvaColors.text,
    success: canvaColors.success,
    error: canvaColors.error,
    warning: canvaColors.warning,
    surface: {
      ...canvaColors.surface,
      background: canvaColors.background.default,
      paper: canvaColors.background.paper,
    },
    interactive: canvaColors.interactive,
    brand: {
      main: canvaColors.primary.main,
      light: canvaColors.primary.light,
      dark: canvaColors.primary.dark,
    },
    customText: {
      main: canvaColors.text.primary,
      light: canvaColors.text.secondary,
      dark: canvaColors.text.primary,
    },
  },

  shape: {
    borderRadius: 12,
  },

  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

    // Headings with tighter line heights
    h1: {
      fontFamily: 'Inter',
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
      color: canvaColors.text.primary,
    },
    h2: {
      fontFamily: 'Inter',
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.25,
      color: canvaColors.text.primary,
    },
    h3: {
      fontFamily: 'Inter',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.3,
      color: canvaColors.text.primary,
    },

    // Body text with improved readability
    body1: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.55,
      color: canvaColors.text.primary,
    },
    body2: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.55,
      color: canvaColors.text.secondary,
    },

    // Custom variants
    headingLarge: {
      fontFamily: 'Inter',
      fontWeight: 800,
      fontSize: '3rem',
      lineHeight: 1.1,
      color: canvaColors.text.primary,
    },
    headingMedium: {
      fontFamily: 'Inter',
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.2,
      color: canvaColors.text.primary,
    },
    headingSmall: {
      fontFamily: 'Inter',
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.3,
      color: canvaColors.text.primary,
    },
    description: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.55,
      color: canvaColors.text.secondary,
    },
    large: {
      fontFamily: 'Inter',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      color: canvaColors.text.primary,
    },
    p: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.55,
      color: canvaColors.text.primary,
    },
    ps: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.5,
      color: canvaColors.text.secondary,
    },
    pt: {
      fontFamily: 'Inter',
      fontWeight: 300,
      fontSize: '0.6875rem',
      lineHeight: 1.45,
      color: canvaColors.text.secondary,
    },
    label: {
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: canvaColors.text.primary,
    },
    labelLarge: {
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.4,
      color: canvaColors.text.primary,
    },
    labelSmall: {
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: '0.6875rem',
      lineHeight: 1.4,
      color: canvaColors.text.primary,
    },
    menuItem: {
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.4,
      color: canvaColors.text.primary,
    },
  },

  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)', // soft shadow 1
    '0px 4px 8px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)', // soft shadow 2
    '0px 8px 16px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.06)', // soft shadow 4
    '0px 12px 24px rgba(0, 0, 0, 0.12), 0px 6px 12px rgba(0, 0, 0, 0.08)',
    '0px 16px 32px rgba(0, 0, 0, 0.14), 0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 20px 40px rgba(0, 0, 0, 0.16), 0px 10px 20px rgba(0, 0, 0, 0.12)',
    '0px 24px 48px rgba(0, 0, 0, 0.18), 0px 12px 24px rgba(0, 0, 0, 0.14)',
    '0px 28px 56px rgba(0, 0, 0, 0.2), 0px 14px 28px rgba(0, 0, 0, 0.16)',
    '0px 32px 64px rgba(0, 0, 0, 0.22), 0px 16px 32px rgba(0, 0, 0, 0.18)',
    '0px 36px 72px rgba(0, 0, 0, 0.24), 0px 18px 36px rgba(0, 0, 0, 0.2)',
    '0px 40px 80px rgba(0, 0, 0, 0.26), 0px 20px 40px rgba(0, 0, 0, 0.22)',
    '0px 44px 88px rgba(0, 0, 0, 0.28), 0px 22px 44px rgba(0, 0, 0, 0.24)',
    '0px 48px 96px rgba(0, 0, 0, 0.3), 0px 24px 48px rgba(0, 0, 0, 0.26)',
    '0px 52px 104px rgba(0, 0, 0, 0.32), 0px 26px 52px rgba(0, 0, 0, 0.28)',
    '0px 56px 112px rgba(0, 0, 0, 0.34), 0px 28px 56px rgba(0, 0, 0, 0.3)',
    '0px 60px 120px rgba(0, 0, 0, 0.36), 0px 30px 60px rgba(0, 0, 0, 0.32)',
    '0px 64px 128px rgba(0, 0, 0, 0.38), 0px 32px 64px rgba(0, 0, 0, 0.34)',
    '0px 68px 136px rgba(0, 0, 0, 0.4), 0px 34px 68px rgba(0, 0, 0, 0.36)',
    '0px 72px 144px rgba(0, 0, 0, 0.42), 0px 36px 72px rgba(0, 0, 0, 0.38)',
    '0px 76px 152px rgba(0, 0, 0, 0.44), 0px 38px 76px rgba(0, 0, 0, 0.4)',
    '0px 80px 160px rgba(0, 0, 0, 0.46), 0px 40px 80px rgba(0, 0, 0, 0.42)',
    '0px 84px 168px rgba(0, 0, 0, 0.48), 0px 42px 84px rgba(0, 0, 0, 0.44)',
    '0px 88px 176px rgba(0, 0, 0, 0.5), 0px 44px 88px rgba(0, 0, 0, 0.46)',
    '0px 92px 184px rgba(0, 0, 0, 0.52), 0px 46px 92px rgba(0, 0, 0, 0.48)',
  ],

  components: {
    // Global CSS baseline
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        '*:focus-visible': {
          outline: `2px solid ${canvaColors.interactive.focus}`,
          outlineOffset: '2px',
        },
      },
    },

    // AppBar with gradient
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${canvaColors.primary.main} 0%, ${canvaColors.secondary.main} 100%)`,
          backgroundSize: '200% 200%',
          animation: 'gradientShift 8s ease infinite',
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
          opacity: 0.06,
          backdropFilter: 'blur(8px)',
        },
      },
    },

    // Toolbar height
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '56px !important',
          '@media (min-width: 600px)': {
            minHeight: '56px !important',
          },
        },
      },
    },

    // Button improvements
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 500,
          gap: 6, // icon spacing
          '&:focus-visible': {
            outline: `2px solid ${canvaColors.interactive.focus}`,
            outlineOffset: '2px',
          },
        },
        sizeMedium: {
          padding: '8px 16px',
          fontSize: '0.875rem',
        },
      },
    },

    // Tabs with rounded indicator
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 4,
          borderRadius: 2,
          backgroundColor: canvaColors.primary.main,
        },
      },
    },

    // Drawer with rounded corners
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: '0 16px 16px 0',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.08), 0px 4px 8px rgba(0, 0, 0, 0.04)',
        },
      },
    },

    // Card defaults to outlined
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          borderColor: canvaColors.surface.border,
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },

    // Slider with larger thumb
    MuiSlider: {
      styleOverrides: {
        thumb: {
          width: 20,
          height: 20,
          '&:hover': {
            boxShadow: '0px 0px 0px 8px rgba(108, 92, 231, 0.16)',
          },
        },
      },
    },

    // Tooltip compact
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.75rem',
          padding: '6px 8px',
          backgroundColor: canvaColors.text.primary,
        },
      },
    },

    // TextField improvements
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: canvaColors.interactive.focus,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: canvaColors.primary.main,
              borderWidth: 2,
            },
          },
        },
      },
    },

    // Chip improvements
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },

    // Menu improvements
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.06)',
          border: `1px solid ${canvaColors.surface.border}`,
        },
      },
    },

    // MenuItem improvements
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: canvaColors.interactive.hover,
          },
        },
      },
    },
  },
})

// Export the theme for backward compatibility
export const theme = editorTheme
